import { useContext } from 'react';
import type { AxiosError, AxiosResponse } from 'axios';
import { useMutation } from '@tanstack/react-query';
import {
  ChatActionType,
  getChatContextMetadata,
  type BrandInsightsInstanceContext,
  type ChatAction,
  type ChatMention,
  type InputAttachment,
} from '@trinity/chat';
import { ChatStateDispatchContext } from '@trinity/chat-components/components/Chat.State/context';
import { useChatApiClient } from '@trinity/chat-components/containers/Chat/useApiClient.js';
import { useActiveApplicationIdentifierNullSafe } from '@trinity/chat-components/containers/UserApplications/useActiveApplicationIdentifier';
import { useActiveBrandIdentifierNullSafe } from '@trinity/chat-components/containers/UserBrands/useActiveBrandIdentifier';
import {
  ActiveChatSessionContext,
  ChatSessionFiltersContext,
  DisplayModeContext,
  SetPendingScrollContext,
  TriniPageContextContext,
} from '../context';

type MutationVars = {
  identifier: string;
  parentQueryIdentifier: string | null;
  query: string;
  action: ChatAction | null;
  context?: BrandInsightsInstanceContext.ItemWithMetadata;
  promptIdentifier?: string;
  mentions?: ChatMention[];
  inputAttachments?: InputAttachment[];
};

export const useSubmitQueryMutation = () => {
  const chat = useContext(ActiveChatSessionContext);
  const filters = useContext(ChatSessionFiltersContext);
  const dispatch = useContext(ChatStateDispatchContext);
  const setPendingScroll = useContext(SetPendingScrollContext);
  const brandIdentifier = useActiveBrandIdentifierNullSafe();
  const applicationIdentifier = useActiveApplicationIdentifierNullSafe();
  const displayMode = useContext(DisplayModeContext);
  const triniPageContext = useContext(TriniPageContextContext);

  const apiClient = useChatApiClient();

  // A Patient Journey picked under Tags is sent as chat context (`metadata.patientJourneyId`), the
  // same place the journey page puts it, so the chat is about that journey.
  const journeyId = (filters.tags ?? []).find((t) => typeof t.metadata?.journeyId === 'number')?.metadata
    ?.journeyId;
  const withJourneyContext = (
    context: BrandInsightsInstanceContext.ItemWithMetadata
  ): BrandInsightsInstanceContext.ItemWithMetadata => {
    if (typeof journeyId !== 'number' || getChatContextMetadata(context)?.patientJourneyId != null) {
      return context;
    }
    return {
      ...context,
      metadata: { ...(getChatContextMetadata(context) ?? {}), patientJourneyId: journeyId },
    } as BrandInsightsInstanceContext.ItemWithMetadata;
  };

  return useMutation({
    mutationKey: ['chat:message:submit'],
    mutationFn: (vars: MutationVars) => {
      const pageContext = triniPageContext
        ? {
            ...triniPageContext,
            url: window.location.href,
            pathname: window.location.pathname,
            pageTitle: document.title,
            capturedAt: new Date().toISOString(),
          }
        : undefined;

      return apiClient.chat.submitChatQuery({
        action: vars.action,
        chatIdentifier: chat.identifier,
        identifier: vars.identifier,
        parentQueryIdentifier: vars.parentQueryIdentifier,
        query: vars.query,
        context: withJourneyContext(vars.context || chat.context),
        filters,
        brandIdentifier,
        applicationIdentifier,
        promptIdentifier: vars.promptIdentifier,
        displayMode,
        mentions: vars.mentions,
        inputAttachments: vars.inputAttachments,
        ...(pageContext && { pageContext }),
      });
    },
    onMutate: ({ action, identifier, parentQueryIdentifier, query, inputAttachments }) => {
      const hideUserMessage =
        action != null &&
        ![
          ChatActionType.ChartQuantAnalyses,
          ChatActionType.SelectHcpSubject,
          ChatActionType.SelectRepSubject,
          ChatActionType.SelectAccountSubject,
          ChatActionType.SearchAccounts,
          ChatActionType.SearchHcps,
          ChatActionType.SearchReps,
        ].includes(action.type);
      dispatch({
        type: 'query-submitted',
        payload: {
          attachments: [],
          chatIdentifier: chat.identifier,
          identifier,
          parentQueryIdentifier,
          value: query,
          hideUserMessage,
          brandIdentifier,
          inputAttachments,
        },
      });
      if (setPendingScroll) {
        setPendingScroll({ force: true });
      }
    },
    onError: (error: AxiosError<AxiosResponse>, vars) => {
      if (error.response?.status === 504 || error.status === 504) {
        dispatch({
          type: 'query-errored',
          payload: {
            langfuseTraceUrl: null,
            queryIdentifier: vars.identifier,
            traceId: null,
          },
        });
        if (setPendingScroll) {
          setPendingScroll({ force: true });
        }
        return;
      }

      if (error.response?.status === 429 || error.status === 429) {
        dispatch({
          type: 'query-errored',
          payload: {
            langfuseTraceUrl: null,
            queryIdentifier: vars.identifier,
            traceId: null,
          },
        });
        if (setPendingScroll) {
          setPendingScroll({ force: true });
        }
        return;
      }

      const traceId = error.response?.headers['x-sentry-trace-id'] as string;
      dispatch({
        type: 'query-errored',
        payload: {
          langfuseTraceUrl: null,
          queryIdentifier: vars.identifier,
          traceId,
        },
      });
      if (setPendingScroll) {
        setPendingScroll({ force: true });
      }
    },
  });
};
